/**
 * Which side of the two-party loop the viewer is on (wave W1.5, the capability-up-front
 * sweep). Author: Mahmoud Ragab Ahmed.
 *
 * A dialog must not offer an act its viewer cannot complete. The server is the authority
 * on every record; this is the coarse half the screen can know before any attempt: the
 * contractor answers (proposes, declares), the operator decides (agrees, approves,
 * verifies). It reads the server-derived workflow class from /api/me. An UNKNOWN class
 * (no answer yet, or an account with no class) keeps the old behaviour and lets the
 * server decide; a class that is neither side (the third party inspector) acts on none.
 */
import { useOutletContext } from "react-router";

import type { Me } from "../api/me";

export type Side = "contractor" | "operator" | "unknown" | "none";

export function sideOf(workflowClass: string | null | undefined): Side {
  if (workflowClass === null || workflowClass === undefined) {
    return "unknown";
  }
  if (workflowClass === "contractor") {
    return "contractor";
  }
  return workflowClass === "tpi" ? "none" : "operator";
}

/** Whether the viewer may be offered an act that belongs to `actor`. */
export function mayActAs(side: Side, actor: "contractor" | "operator"): boolean {
  if (side === "unknown") {
    return true;
  }
  return side === actor;
}

/** The viewer's side, from the shell's outlet context; unknown outside the shell. */
export function useViewerSide(): Side {
  const context = useOutletContext<{ me?: Me | null } | undefined>();
  return sideOf(context?.me?.workflowClass);
}
